import { Component, DestroyRef, effect, input, OnInit, output, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { UntypedFormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { Profile, UserService } from '@eadsysteme/ewsauthclient-v1';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { Icons } from '../../../util/Icons';
import { LayoutService } from '../../../../@vex/services/layout.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LogoutService } from '../../../../@vex/services/logout.service';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { DefaultClassDirective } from '@ngbracket/ngx-layout/extended';

@Component({
	selector: 'vex-update-profile',
	templateUrl: './update-profile.component.html',
	styleUrl: './update-profile.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslocoDirective, MatFormField, NgClass, DefaultClassDirective, MatLabel, MatInput, ReactiveFormsModule, MatError],
})
export class UpdateProfileComponent implements OnInit {
	translocoService = inject(TranslocoService);
	userService = inject(UserService);
	layoutService = inject(LayoutService);
	snackbar = inject(SnackbarService);
	icons = inject(Icons);
	keycloak = inject(KeycloakService);
	private logoutService = inject(LogoutService);
	private dialog = inject(MatDialog);
	private destroyRef = inject(DestroyRef);

	firstNameFC = new UntypedFormControl({ value: '', disabled: true }, [Validators.required, Validators.minLength(1)]);
	lastNameFC = new UntypedFormControl({ value: '', disabled: true }, [Validators.required, Validators.minLength(1)]);
	isMobile = signal(false);

	isEdit = input<boolean>();
	readonly update = input<Observable<void>>(undefined);

	allowUpdate = output<boolean>();
	formChanged = output<boolean>();

	constructor() {
		effect(() => {
			if (this.isEdit()) {
				this.firstNameFC.enable();
				this.lastNameFC.enable();
			} else {
				this.firstNameFC.disable();
				this.lastNameFC.disable();
			}
		});
	}

	ngOnInit(): void {
		this.isMobile.set(this.layoutService.isMobile());
		const keycloakToken = this.keycloak.getKeycloakInstance().tokenParsed;
		this.firstNameFC.setValue(keycloakToken['given_name']);
		this.lastNameFC.setValue(keycloakToken['family_name']);

		this.update()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.updateProfile();
			});
	}

	validateInput() {
		this.allowUpdate.emit(this.firstNameFC.valid && this.lastNameFC.valid);
		this.formChanged.emit(this.firstNameFC.dirty || this.lastNameFC.dirty);
	}

	updateProfile() {
		const profileDto: Profile = {
			firstName: this.firstNameFC.value,
			lastName: this.lastNameFC.value,
		};
		const userId = this.keycloak.getKeycloakInstance().tokenParsed.sub;
		this.userService.updateOwnProfile(userId, profileDto).subscribe({
			next: (response) => {
				if (response) {
					// Show Confim dialog and logout user
					this.notifyUserAndLogout();
				} else {
					this.snackbar.error(this.translocoService.translate('userProfile.profileUpdateBadInput'));
				}
			},
			error: () => {
				this.snackbar.error(this.translocoService.translate('errors.profileUpdate'));
			},
		});
	}

	notifyUserAndLogout() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.showCloseButton = false;
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('userProfile.profileUpdatedSuccessfully');
		dialogRef.componentInstance.confirmButtonText = this.translocoService.translate('common.ok');

		dialogRef.afterClosed().subscribe(() => {
			this.logoutService.logout();
		});
	}
}
